import { Box, Grid } from "@mui/material";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { getBackendErrors, getErrorMessage } from "api/errors";
import { hasViewAccessToResource, useSettings } from "api/general";
import { useRoles } from "api/roles";
import { FormButtons } from "components/FormControls/FormButtons";
import { FormSection } from "components/FormControls/FormSection";
import { RequiredFieldLabel } from "components/FormControls/RequiredFieldLabel";
import { SelectInput } from "components/FormControls/Select/Select";
import { TextInput } from "components/FormControls/TextInput";
import { TopHeader } from "components/Header/TopHeader/TopHeader";
import { LeaveSiteGuard } from "components/Tooltips/LeaveSiteGuard";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { useNotifications } from "contexts/Toast/ToastProvider";
import { Form, Formik } from "formik";
import { useMessage } from "hooks/intl";
import { useManageInfiniteScroll } from "hooks/useManageInfiniteScroll";
import { useState } from "react";
import "react-loading-skeleton/dist/skeleton.css";
import { useNavigate } from "react-router-dom";
import { backendErrors } from "utilities/backendErrors";
import { focusError } from "utilities/focusError";
import { links } from "utilities/links";

const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    formContainer: {
      margin: `${theme.spacing(8)} auto 0`,
      maxWidth: "872px",
    },
  }),
);

export function AdminData() {
  const classes = useStyles();
  const { notify } = useNotifications();
  const navigate = useNavigate();
  const { message } = useMessage();
  const { useAdminData, editAdmin } = useSettings();
  const { data: adminData } = useAdminData();
  const { useInfiniteRolesList } = useRoles();
  const [searchedPhrase, setSearchedPhrase] = useState("");

  const {
    data: infiniteRolesList,
    size: rolesSize,
    setSize: setRolesSize,
    isValidating,
  } = useInfiniteRolesList({
    filters: searchedPhrase
      ? [
          {
            condition: "like",
            query: "name",
            value: searchedPhrase,
          },
        ]
      : [],
    order: "asc",
    sortKey: "name",
  });

  const { infiniteData: rolesList, anyElementsToFetch: anyRolesToFetch } =
    useManageInfiniteScroll(infiniteRolesList || []);

  const formattedRoleList = rolesList.map(
    (role: { id: string; name: string }) => ({
      id: role?.id.toString(),
      label: role?.name,
    }),
  );

  const hasViewAdminPermission = hasViewAccessToResource("ADMIN");

  const initialValues = {
    email: adminData?.email,
    firstName: adminData?.firstName,
    lastName: adminData?.lastName,
    phone: adminData?.phone,
    roles: adminData?.roles?.[0]?.id,
  };

  const isFormDataReady = adminData && initialValues;
  const adminRoleID = adminData.roles?.[0]?.id;
  const adminRoleName = adminData.roles?.[0]?.name;

  return (
    <>
      <TopHeader title={message({ id: "editProfile" })} />
      <div className={classes.formContainer}>
        <Formik
          enableReinitialize
          initialStatus={{}}
          initialValues={initialValues}
          onSubmit={async (values, { setSubmitting, setStatus }) => {
            setSubmitting(true);
            setStatus({});
            try {
              const response = await editAdmin(adminData.id, {
                email: values?.email,
                firstName: !!values.firstName ? values?.firstName : undefined,
                lastName: !!values.lastName ? values?.lastName : undefined,
                phone: !!values.phone ? values?.phone : undefined,
                roles: [Number(adminRoleID)],
              });
              if (response?.data) {
                navigate(links.settings, { state: { activeTab: "admins" } });
                notify(message({ id: "profileEditedSuccessfully" }));
              }
            } catch (exception) {
              setSubmitting(false);
              notify(getErrorMessage(exception));
              const errors = backendErrors(getBackendErrors(exception));
              setStatus(errors);
              focusError();
            }
          }}
        >
          {({
            handleSubmit,
            isSubmitting,
            status,
            values,
            dirty,
            initialValues,
          }) => {
            const formError = status?.[""] || status?.admin;
            return (
              <Form onSubmit={handleSubmit}>
                <FormSection
                  isLoading={!isFormDataReady}
                  sectionError={formError}
                  title={message({ id: "basicInformation" })}
                >
                  <Grid container spacing={10}>
                    <Grid xs={6} item>
                      <TextInput
                        data-test-id="edit-profile-name-input"
                        disabled={hasViewAdminPermission}
                        label={message({ id: "firstName" })}
                        name="firstName"
                        width="100%"
                      />
                    </Grid>
                    <Grid xs={6} item>
                      <TextInput
                        data-test-id="edit-profile-last-name-input"
                        disabled={hasViewAdminPermission}
                        label={message({ id: "lastName" })}
                        name="lastName"
                        width="100%"
                      />
                    </Grid>
                    <Grid xs={6} item>
                      <TextInput
                        data-test-id="edit-profile-email-input"
                        disabled={hasViewAdminPermission}
                        label={message({ id: "email" })}
                        name="email"
                        width="100%"
                      />
                    </Grid>
                    <Grid xs={6} item>
                      <TextInput
                        data-test-id="edit-profile-phone-number-input"
                        disabled={hasViewAdminPermission}
                        label={message({ id: "phoneNumber" })}
                        name="phone"
                        width="100%"
                      />
                    </Grid>
                    <Grid xs={12} item>
                      <SelectInput
                        asyncSearch={(searchedQuery: string) =>
                          setSearchedPhrase(searchedQuery)
                        }
                        disabled={hasViewAdminPermission}
                        data-test-id="edit-profile-roles-input"
                        displaySearch
                        initialValue={{ id: adminRoleID, label: adminRoleName }}
                        items={formattedRoleList}
                        label={
                          <RequiredFieldLabel text={message({ id: "role" })} />
                        }
                        name="roles"
                        placeholder={message({ id: "searchForRole" })}
                        width="100%"
                        anyElementsToFetch={anyRolesToFetch}
                        loadMoreElements={() =>
                          !isValidating && setRolesSize(rolesSize + 1)
                        }
                      />
                    </Grid>
                  </Grid>
                </FormSection>
                <Box mt={12} />
                <FormButtons
                  disableSubmit={
                    isSubmitting || hasViewAdminPermission || !isFormDataReady
                  }
                  onCancelClick={() => navigate(links.dashboard)}
                  onSubmitClick={() => handleSubmit()}
                />
                {!isSubmitting && (
                  <LeaveSiteGuard
                    warnBeforeRefresh
                    isDirty={dirty}
                    initialValues={initialValues}
                    currentValues={values}
                  />
                )}
              </Form>
            );
          }}
        </Formik>
      </div>
    </>
  );
}
